import { Link } from "react-router-dom";
import { Flame, Dumbbell, Users, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { GreekKeyDivider } from "@/components/GreekKeyDivider";
import { GreekWatermark } from "@/components/GreekWatermark";

const HERO_IMG = "/gallery/rings.jpg";

const PROGRAMS = [
  {
    icon: Flame,
    title: "Functional Fitness",
    tag: "All levels",
    desc: "Our signature daily workout — constantly varied, functional movement performed at high intensity. Every session is scaled to your level by a coach on the floor.",
    image: "/gallery/front-squat.jpg",
  },
  {
    icon: Dumbbell,
    title: "Olympic Lifting",
    tag: "Intermediate+",
    desc: "Snatch, clean & jerk, and the accessory strength work behind them. Small class sizes so every lift gets a coaching cue.",
    image: "/gallery/coaching.jpg",
  },
  {
    icon: Sparkles,
    title: "Super Saturday",
    tag: "All levels",
    desc: "A bigger, social team session that closes out the training week — partner workouts, community energy, no two Saturdays the same.",
    image: "/gallery/pull-ups.jpg",
  },
  {
    icon: Users,
    title: "Engine",
    tag: "All levels",
    desc: "Sunday conditioning built purely to build your aerobic base — rowing, running, biking, and the engine work everything else relies on.",
    image: "/programs/engine-athlete.jpg",
  },
];

export default function Programs() {
  return (
    <div>
      <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Programs" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void to-void" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GreekWatermark letter="Α" className="-top-10 -left-6 sm:-left-10" />
          <p className="eyebrow mb-3" data-testid="programs-eyebrow">Programs</p>
          <h1 className="font-anton uppercase tracking-tighter text-cream text-5xl sm:text-7xl max-w-3xl">
            Every path up the mountain
          </h1>
          <p className="text-smoke mt-6 max-w-xl text-base sm:text-lg">
            Whichever door you walk through, our coaches meet you where you are — and push from there.
          </p>
        </div>
        <GreekKeyDivider className="absolute bottom-0 left-0" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 space-y-6">
        {PROGRAMS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.06}>
            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-0 border border-line bg-surface ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
              data-testid={`program-card-${i}`}
            >
              <div className="h-64 sm:h-80 lg:h-full overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center [direction:ltr]">
                <div className="flex items-center gap-3 mb-4">
                  <p.icon className="h-6 w-6 text-gold" />
                  <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-2 py-1">{p.tag}</span>
                </div>
                <h2 className="font-anton uppercase text-cream text-3xl sm:text-4xl mb-4">{p.title}</h2>
                <p className="text-smoke leading-relaxed">{p.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-surface border-t border-line py-20 sm:py-28 text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <Reveal>
            <h2 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-5xl">Ready to find your program?</h2>
            <Button asChild size="lg" className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider h-14 px-10 mt-8" data-testid="programs-cta-schedule-button">
              <Link to="/#timetable">View the Timetable <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
